import { useRef, useState, useEffect, Fragment } from 'react';
import Modal from '@sdk/components/ui/modal/Modal';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import ModalFooterActionButtons from '@sdk/components/ui/modal/ModalFooterActionButtons';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Text14 from '@sdk/components/ui/typography/Text14';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import RadioButton from '@sdk/components/forms/Radiobutton';
import numberOrdinal from '@sdk/utils/numberOrdinal';

import BreakYellowSvg from '@web/assets/svg/Icons/32/break-yellow.svg';

type Props = {
  isVisible: boolean;
  accept: (num: number) => void;
  decline: () => void;
  arg?: {
    breakType: string;
    breakNumbers: number[];
  };
};

const BREAK_NUMBER_FIELD_NAME = 'attestation-break-number-radio-field';

export const ChooseAttestationToReplaceModal = ({ isVisible, accept, decline, arg }: Props) => {
  const config = useRef({ noDismiss: true }).current;
  const [selectedBreakNumber, setSelectedBreakNumber] = useState<string>();

  const { breakType, breakNumbers = [] } = arg ?? {};

  useEffect(() => {
    if (isVisible && breakNumbers?.length) {
      setSelectedBreakNumber(String(breakNumbers[0]));
    }
  }, [isVisible, breakNumbers]);

  return (
    <Modal fixed config={config} isVisible={isVisible}>
      <ModalBodyWithHeader
        width={620}
        titleThemeColor={ThemeColorType.warning}
        titleText={gettext('Add break')}
        titleIcon={<BreakYellowSvg />}
        hideClose>
        {breakNumbers.length > 1 ? (
          <Column
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.name === BREAK_NUMBER_FIELD_NAME) {
                setSelectedBreakNumber(e.target.value);
              }
            }}>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext(
                'Which of the %(count)s missed %(breakType)s breaks would you like to add?',
                {
                  count: breakNumbers.length,
                  breakType,
                }
              )}
            </Text14>
            <Row mTop={10}>
              <HorizontalSeparator />
            </Row>
            {breakNumbers.map((breakNumber) => (
              <Fragment key={`${selectedBreakNumber}_${breakNumber}`}>
                <Row pTop={20} pBottom={20}>
                  <RadioButton
                    fieldName={BREAK_NUMBER_FIELD_NAME}
                    value={String(breakNumber)}
                    label={gettext('%(breakNumber)s %(breakType)s break', {
                      breakType,
                      breakNumber: numberOrdinal(breakNumber),
                    })}
                    checked={selectedBreakNumber === String(breakNumber)}
                  />
                </Row>
                <HorizontalSeparator />
              </Fragment>
            ))}
          </Column>
        ) : (
          <Text14 themeColor={ThemeColorType.gray}>
            {gettext('Psst! Added break will replace the missed %(breakType)s break.', {
              breakType,
            })}
          </Text14>
        )}
        <ModalFooterActionButtons
          mTop={20}
          cancel={decline}
          submit={() => accept(Number(selectedBreakNumber))}
          isSubmitAsync={false}
          cancelText={gettext('Back')}
          submitText={gettext('Add break')}
        />
      </ModalBodyWithHeader>
    </Modal>
  );
};
